import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Page from '../../../components/PageWrapper';
import clsx from 'clsx';
import RedactInfo from '../../../components/elements/RedactInfo';
import ToolTip from '../../../components/ToolTip';
import moment from 'moment';
import useRoute from '../../../hooks/routesHook';
import {
  capitalizeSentence,
  getFormattedDate,
  getFormattedPaymentChannel,
  getFormattedSentence,
  getPhoneNumberFromShortCodeFormat,
  isOfflinePayment,
  removeUnderscoresAndCapitalize,
  showToast,
  sortArrayByAlphabets,
} from '../../../utils/miscFunctions';
import {
  PAYMENT_STATUS_STATES,
  POLICY_REVIEW_STATES,
  REDACT_INFO_TYPES,
  COMPANY_TYPES,
  UPLOAD_IMAGE_ENDPOINTS,
  UPLOADABLE_DOCUMENT_TYPES
} from '../../../resources/enums';
import useUserAccount from '../../../hooks/userAccountHook';
import {
  generatePremiumFinancingPayments,
  getPaymentLinkForPremiumFinancing,
} from '../../../api/requests';
import BackButton from '../../../components/BackButton';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Loader from '../../../components/Loader';
import GTable from '../../../components/elements/GTable';
import TabContent from '../../../components/tabs/TabContent';
import { INPUT_DATE_FORMAT } from '../../../resources/constants';
import PaymentStatus from '../../../components/PaymentStatus';
import TabNavItem from '../../../components/tabs/TabNavItem';
import ActivityStepper from '../../../components/ActivityStepper';
import {
  BadgeCheck,
  CalendarClock,
  CheckCircle,
  ClipboardList,
  CreditCard,
  Download,
  Edit,
  Eye,
  FileSearch,
  FileText,
  Handshake,
  Paperclip,
  Pencil,
  RotateCcw,
  RotateCw,
  Send,
  Share2,
  ShieldCheck,
  Upload,
  UserPlus,
  Wallet,
  WandSparkles
} from 'lucide-react';
import Button from '../../../components/form-control/Button';
import PaymentAnalysis from '../../../components/PaymentAnalysis';
import ActionsPopOnMe from '../../../components/ActionsPopOnMe';
import { LuCalendarClock } from 'react-icons/lu';
import { BsShield } from 'react-icons/bs';
import CustomerInfo from '../../../components/CustomerInfo';
import usePremiumFinancing from '../../../hooks/premiumFinancingHook';
import useModal from '../../../hooks/modalHook';
import { IPayment } from '../../../models/payment/IPayment';
import { ITableAction } from '../../../components/elements/GTable/GTable';
import PopOnMe from '../../../components/elements/poponme';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import usePolicy from '../../../hooks/policyHook';
import PreviewImage from '../../../components/elements/PreviewImage';

interface IPremiumFinancingApplicationDetails {
  previousPath: string;
}

const PremiumFinancingApplicationDetails = ({ previousPath }: IPremiumFinancingApplicationDetails) => {
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const { user } = useUserAccount();
  const params = useParams();
  const applicationId = params?.id;
  const queryClient = useQueryClient();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [activeTab, setActiveTab] = useState('payments');
  const pageRef = useRef<HTMLDivElement>(null);
  const {
    FinancingModal,
    premiumFinancingPreapproval,
    editReviewStage,
    showPaymentLinkModal,
    showEditPaymentModal,
    getDocuments,
    generateDocuments,
    financingApplication,
    paymentAnalysis,
    payments,
    loading,
    displayFinancingApplication,
    allApplicationPayments,
    runPaymentAnalysis,
    InitiateHubtelDirectCharge,
    handleResetPayment,
    sendPaymentNotification,
    showPaymentApprovalModal,
    loadingPayment,
    approveApplication,
    handleGeneratePremiumFinancingPayments,
    sendAgreementFormToCustomer
  } = usePremiumFinancing();
  const { uploadPolicyDocument, PolicyModalComponent } = usePolicy();
  const balance = () => {
    return financingApplication?.plan && payments
      ? (financingApplication?.plan?.totalAmount - totalPaymentsAmount()).toFixed(2)
      : 0;
  };
  const orderPaymentsById = payments && payments.sort((a: any, b: any) => a.id - b.id);
  const localDate = new Date();
  const paymentDate = localDate ? moment(localDate).format(INPUT_DATE_FORMAT) : '';
  const documentList = [
    UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT,
    UPLOADABLE_DOCUMENT_TYPES.ID_CARD,
    UPLOADABLE_DOCUMENT_TYPES.ID_CARD_BACK,
    UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO,
    UPLOADABLE_DOCUMENT_TYPES.ROADWORTHY,
  ]
  const documentListFormatted = documentList.map((doc) => removeUnderscoresAndCapitalize(doc))


  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['displayFinancingApplication'] });
  };
  const invalidateQueriesPayments = () => {
    queryClient.invalidateQueries({ queryKey: ['allApplicationPayments'] });
  };

  useQuery<any, Error>({
    queryKey: ['getDocuments'],
    queryFn: () => getDocuments()
  });

  useQuery<any, Error>({
    queryKey: ['displayFinancingApplication'],
    queryFn: () => displayFinancingApplication()
  });

  useQuery({
    queryKey: ['allApplicationPayments', financingApplication],
    queryFn: () => allApplicationPayments()
  });

  const totalPaymentsAmount = () => {
    const amount =
      payments &&
      payments?.map((payment: any) => {
        return payment?.paymentStatus == PAYMENT_STATUS_STATES.SUCCESS && payment?.amount;
      });

    let startVal = 0;
    let x = 0;
    const amountLength = amount?.length ? amount?.length : 0;
    while (x < amountLength) {
      startVal = startVal + amount[x];
      x++;
    }

    return startVal;
  };

  const handlePaymentEvidencePreview = (payment: IPayment) => {
    window.open(payment?.evidenceUrl, "_blank")
  }

  const canApprovePayment = (payment: IPayment) => {
    return (
      payment?.paymentStatus == PAYMENT_STATUS_STATES.APPROVAL &&
      (user.isInternalOwner() || (user.isBroker() && !user.isAgent()))
    );
  }

  const getRowActions = (payment: IPayment): ITableAction[] => {
    const actions = [];

    if (payment && payment?.paymentStatus !== PAYMENT_STATUS_STATES.SUCCESS && payment?.paymentStatus !== PAYMENT_STATUS_STATES.VOID && user?.isInternalAdmin()) {
      actions.push(
        {
          label: 'Reset payment',
          icon: <RotateCw className='h-4 w-4' />,
          callback: () => {
            if (payment) {
              handleResetPayment(payment, invalidateQueries);
            }
          }
        }
      )
    }
    if (payment && payment?.paymentStatus !== PAYMENT_STATUS_STATES.SUCCESS && payment?.paymentStatus !== PAYMENT_STATUS_STATES.VOID) {
      actions.push(
        {
          label: 'Get payment link',
          icon: <Paperclip className='h-4 w-4' />,
          callback: () => {
            if (payment) {
              getPaymentLinkForPremiumFinancing(Number(payment?.id))
                .then((response: any) => {
                  showToast.success(response?.message,);
                  const paymentLink = response?.data;
                  const paymentId = Number(payment.id)
                  showPaymentLinkModal(paymentLink, paymentId);
                })
                .catch((error: any) => {
                  showToast.error(error?.description || 'Failed to get payment link');
                });
            }

          }
        },
        {
          label: 'Send payment notification',
          icon: <Send className='h-4 w-4' />,
          callback: () => {
            if (payment) sendPaymentNotification(payment);
          }
        },
        {
          label: 'Initiate direct charge',
          icon: <CreditCard className='h-4 w-4' />,
          callback: () => {
            if (payment) {
              InitiateHubtelDirectCharge(payment);
            }
          }
        },
        {
          label: 'Edit',
          icon: <Edit className="h-4 w-4" />,
          callback: () => {
            if (payment) showEditPaymentModal(payment);
          }
        },

      );


      if (user.isInternalSupervisor() || user.isInternalOwner()) {
        // if (user.isReviewer() || user.isSuperAdmin()) {
        if (payment.evidenceUrl) {
          actions.push({
            label: 'View evidence',
            icon: <Eye className="h-4 w-4" />,
            callback: () => {
              if (payment) handlePaymentEvidencePreview(payment);
            }
          })
        }
      }



      if (canApprovePayment(payment))
        actions.push({
          label: 'Approve',
          icon: <CheckCircle className='h-4 w-4' />,
          callback: () => {
            if (payment) showPaymentApprovalModal(payment, invalidateQueries);
          }
        });
    }

    return actions;
  };

  const uploadDoc = () => {
    return financingApplication?.applicationNumber &&
      uploadPolicyDocument(String(financingApplication?.applicationNumber),
        'displayFinancingApplication',
        documentListFormatted,
        UPLOAD_IMAGE_ENDPOINTS.DOCUMENT,
        'auto',
        []);
  }

  const getButtonActions = () => {
    const actions = [
    ];

    if (financingApplication) {
      if (financingApplication?.reviewStage == POLICY_REVIEW_STATES.IN_APPROVAL && user.isInternalOwner()) {
        actions.push({
          label: `Approve`,
          callback: () => {
            approveApplication();
          },
          icon: <BadgeCheck className="h-4 w-4" />
        },);
      }

      // if (financingApplication?.reviewStage !== POLICY_REVIEW_STATES.APPROVED) {
      //   actions.push({
      //     label: `Review application`,
      //     callback: () => {
      //       editReviewStage(financingApplication);
      //     },
      //     icon: <FileSearch className="h-4 w-4" />
      //   });
      // }
    }
    return sortArrayByAlphabets(actions);
  };

  const getActions = () => {
    const actions = [
      {
        label: 'Upload document',
        icon: <Upload className="h-4 w-4" />,
        callback: () => uploadDoc()
      },
      {
        label: 'Share agreement document',
        icon: <Share2 className="h-4 w-4" />,
        callback: () => sendAgreementFormToCustomer()
      },
    ];

    if (financingApplication) {
      actions.push(
        {
          label: 'Generate payment schedules',
          callback: () => {
            handleGeneratePremiumFinancingPayments(paymentDate, invalidateQueriesPayments);
          },
          icon: <CalendarClock className="h-4 w-4" />
        });

      if (financingApplication?.reviewStage == POLICY_REVIEW_STATES.IN_APPROVAL && user.isInternalOwner()) {
        actions.push({
          label: `Approve`,
          callback: () => approveApplication(),
          icon: <BadgeCheck className="h-4 w-4" />
        });
      }

      // if (financingApplication?.reviewStage !== POLICY_REVIEW_STATES.APPROVED) {
      //   actions.push({
      //     label: `Review application`,
      //     callback: () => {
      //       editReviewStage(financingApplication);
      //     },
      //     icon: <FileSearch className="h-4 w-4" />
      //   });
      // }
    }

    if (user.company?.type !== COMPANY_TYPES.FINANCIER && financingApplication?.reviewStage !== POLICY_REVIEW_STATES.APPROVED) {
      actions.push({
        label: 'Edit',
        callback: () => {
          navigate(`${getDashboardBase()}/premium-financing/${applicationId}/edit`, {
            replace: true
          });
        },
        icon: <Pencil className="h-4 w-4" />
      });
    }

    return actions;
  };

  const getProposer = (): JSX.Element => {
    return (
      <PremiumFinancingDetailsItemCard
        title={'Proposer'}
        icon={<UserPlus className="w-5 h-5 mr-2" />}>
        <>
          <div className="bg-gray-50 p-4 rounded-2xl h-full dark:bg-transparent dark:border dark:border-darkBg-border">
            <PremiumFinancingDetailsItem
              name="Name"
              value={
                financingApplication?.customer?.firstName &&
                  financingApplication?.customer?.lastName
                  ? capitalizeSentence(
                    financingApplication?.customer?.firstName +
                    ' ' +
                    financingApplication?.customer?.lastName
                  )
                  : '-'
              }
            />

            <PremiumFinancingDetailsItem
              name="Phone"
              value={
                <RedactInfo
                  infoType={REDACT_INFO_TYPES.PHONE}
                  info={
                    financingApplication?.customer?.phone ? getPhoneNumberFromShortCodeFormat(financingApplication?.customer?.phone) : '-'
                  }
                  dataId={financingApplication?.ref ?? ''}
                  dataType="policy"
                  clickable={!user.isInsurer() && true}
                />
              }
            />

            <PremiumFinancingDetailsItem
              name="Other Phone"
              value={
                financingApplication?.customer?.otherPhone ? (
                  <RedactInfo
                    infoType={REDACT_INFO_TYPES.PHONE}
                    info={
                      getPhoneNumberFromShortCodeFormat(
                        financingApplication?.customer?.otherPhone
                      ) ?? '-'
                    }
                    dataId={financingApplication?.ref ?? ''}
                    dataType="policy"
                    clickable={!user.isInsurer() && true}
                  />
                ) : financingApplication?.customer?.otherPhone ? (
                  getPhoneNumberFromShortCodeFormat(financingApplication?.customer?.otherPhone)
                ) : (
                  '-'
                )
              }
            />

            <PremiumFinancingDetailsItem
              name="Email"
              value={
                financingApplication?.customer.email ? financingApplication?.customer.email : '-'
              }
            />

            <PremiumFinancingDetailsItem name="Address">
              <ToolTip
                message={
                  <RedactInfo
                    info={
                      financingApplication?.customer?.address
                        ? financingApplication?.customer?.address
                        : '-'
                    }
                    totalToRedact={10}
                    dataId={financingApplication?.ref ?? ''}
                    dataType="policy"
                  />
                }>
                <div className="font-medium truncate w-full max-w-32">
                  {financingApplication?.customer?.address && financingApplication?.active ? (
                    <RedactInfo
                      info={
                        financingApplication?.customer?.address
                          ? financingApplication?.customer?.address
                          : '-'
                      }
                      totalToRedact={10}
                      dataId={financingApplication?.ref ?? ''}
                      dataType="policy"
                    />
                  ) : financingApplication?.customer?.address ? (
                    financingApplication?.customer?.address
                  ) : (
                    '-'
                  )}
                </div>
              </ToolTip>
            </PremiumFinancingDetailsItem>

            <PremiumFinancingDetailsItem
              name="Date of Birth"
              value={
                financingApplication?.customer?.dateOfBirth ? (
                  <RedactInfo
                    info={getFormattedDate({ date: financingApplication?.customer.dateOfBirth })}
                    totalToRedact={6}
                    dataId={financingApplication?.ref ?? ''}
                    dataType="policy"
                    clickable={!user.isInsurer() && true}
                  />
                ) : (
                  '-'
                )
              }
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-2xl h-full dark:bg-transparent dark:border dark:border-darkBg-border">
            <PremiumFinancingDetailsItem
              name="ID Type"
              value={
                financingApplication?.customer?.idType
                  ? capitalizeSentence(financingApplication?.customer?.idType)
                  : '-'
              }
            />

            {!user.isInsurer() && (
              <PremiumFinancingDetailsItem
                name="ID Expiry Date"
                value={getFormattedDate({ date: financingApplication?.customer?.idExpiry ?? '-' })}
              />
            )}

            <PremiumFinancingDetailsItem
              name="Occupation"
              value={financingApplication?.customer?.occupation ?? '-'}
            />

            <PremiumFinancingDetailsItem
              name="Employer Name"
              value={financingApplication?.customer?.employerName ?? '-'}
            />

            <PremiumFinancingDetailsItem
              name="Employer Address"
              value={
                financingApplication?.customer?.employerAddress
                  ? financingApplication?.customer?.employerAddress
                  : '-'
              }
            />
            <PremiumFinancingDetailsItem
              name="Employer Phone Number"
              value={
                financingApplication?.customer?.employerPhone ? getPhoneNumberFromShortCodeFormat(financingApplication?.customer?.employerPhone) : '-'
              }
            />

            <PremiumFinancingDetailsItem
              name="Monthly Income Band"
              value={financingApplication?.customer?.monthlyIncomeBand ?? '-'}
            />
          </div>
        </>
      </PremiumFinancingDetailsItemCard>
    );
  };

  const getPaymentPlan = () => {
    return (
      <PremiumFinancingDetailsItemCard
        title="Payment Plan"
        classes="grid grid-cols-1"
        icon={<ClipboardList className="w-5 h-5 mr-2" />}>
        <div className="p-2 h-[260px]">
          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Created On'}
            value={
              financingApplication?.plan?.createOn
                ? getFormattedDate({ date: financingApplication?.plan?.createOn })
                : '-'
            }
          />

          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Initial Deposit'}
            value={
              financingApplication?.plan?.initialDeposit
                ? financingApplication?.plan?.initialDeposit
                : '-'
            }
          />
          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Frequency'}
            value={financingApplication?.plan?.period ? financingApplication?.plan?.period : '-'}
          />

          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Installment Amount'}
            value={
              financingApplication?.plan?.installmentAmount
                ? financingApplication?.plan?.installmentAmount
                : '-'
            }
          />
          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Total Amount'}
            value={
              financingApplication?.plan?.totalAmount
                ? financingApplication?.plan?.totalAmount
                : '-'
            }
          />
          <PremiumFinancingDetailsItem
            classes="!flex-row justify-between items-center"
            name={'Outright Premium'}
            value={
              financingApplication?.premium
                ? financingApplication?.premium
                : '-'
            }
          />
        </div>
      </PremiumFinancingDetailsItemCard>
    );
  };

  const getFinancedPartner = () => {
    return (
      <PremiumFinancingDetailsItemCard
        title="Financed Partner"
        classes="grid grid-cols-1"
        icon={<Handshake className="w-5 h-5 mr-2" />}>
        <div className="p-2 h-[260px]">
          <PremiumFinancingDetailsItem
            name={'Name'}
            value={
              financingApplication?.financier?.name ? financingApplication?.financier?.name : '-'
            }
          />
          <PremiumFinancingDetailsItem
            name={'Email'}
            value={
              financingApplication?.financier?.email ? financingApplication?.financier?.email : '-'
            }
          />
          <PremiumFinancingDetailsItem
            name={'Contact'}
            value={
              financingApplication?.financier?.mobilePhone
                ? getPhoneNumberFromShortCodeFormat(financingApplication?.financier?.mobilePhone)
                : '-'
            }
          />
          <PremiumFinancingDetailsItem
            name={'Country'}
            value={
              financingApplication?.financier?.countryName
                ? financingApplication?.financier?.countryName
                : '-'
            }
          />
        </div>
      </PremiumFinancingDetailsItemCard>
    );
  };

  const getUnderwriterPartner = () => {
    return (
      <PremiumFinancingDetailsItemCard
        title="Underwriter"
        classes="grid grid-cols-1"
        icon={<FileText className="w-5 h-5 mr-2" />}>
        <div className="p-2 h-[260px]">
          <PremiumFinancingDetailsItem
            name={'Name'}
            value={financingApplication?.insurer?.name}
          />
          <PremiumFinancingDetailsItem
            name={'Email'}
            value={financingApplication?.insurer?.email}
          />
          <PremiumFinancingDetailsItem
            name={'Contact'}
            value={getPhoneNumberFromShortCodeFormat(financingApplication?.insurer?.phoneNumber ?? '-')}
          />
          <PremiumFinancingDetailsItem
            name={'Country'}
            value={financingApplication?.insurer?.country?.name}
          />
        </div>
      </PremiumFinancingDetailsItemCard>
    );
  };

  const openPictureModal = (imgURL: string) => {

    imgURL && updateModalStates({
      children: (
        <PreviewImage onClose={closeModal} imgURL={imgURL} />
      ),
      show: true,
      size: '3xl',
      showTitleSection: false,
      hidePanelStyles: true,
      classes: "!px-0  !rounded-none"
    });
  };

  const getDocumentUpload = () => {
    return (
      <PremiumFinancingDetailsItemCard
        title="Document(s)"
        classes="grid grid-cols-1"
        action={() => uploadDoc()}
        icon={<FileText className="w-5 h-5 mr-2" />}>
        {financingApplication && financingApplication.documents.length > 0 ? (
          <div className="p-2 rounded-2xl h-auto">
            <ul className="divide-y divide-gray-200 dark:divide-darkBg-border">
              {financingApplication.documents.map((document) => (
                <li key={document.id} className="py-3 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">
                        {getFormattedSentence(document.docType)}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {document.docType === 'PAYMENT_AGREEMENT' &&
                      <button className="text-gray-400 hover:text-gray-500">
                        <Share2
                          onClick={() => sendAgreementFormToCustomer()}
                          className="h-4 w-4"
                        />
                      </button>}
                    <button className="text-gray-400 hover:text-gray-500">
                      <Eye
                        onClick={() => document?.docUrl && document?.docUrl.includes('.pdf')
                          ? window.open(`${document.docUrl}`, '_blank')
                          : openPictureModal(String(document?.docUrl))}
                        className="h-4 w-4"
                      />
                    </button>
                    <button className="text-gray-400 hover:text-gray-500">
                      <Download
                        onClick={() => window.open(`${document.docUrl}`, '_blank')}
                        className="h-4 w-4"
                      />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col gap-5 items-center justify-center text-gray-200 dark:text-darkBg-mainText">
            <h3>No document found</h3>
            <Button
              label="Generate Documents"
              className="bg-primary-main w-fit text-black  !py-2 px-10 text-sm font-bold rounded-md"
              onClick={generateDocuments}
              icon={<WandSparkles className="w-5 h-5 mr-2" />}
            />
          </div>
        )}
      </PremiumFinancingDetailsItemCard>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div className="">
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div className="">
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'ID',
            accessor: 'id',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Due Date',
            accessor: 'dueDate',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Paid Date',
            accessor: 'dateOfPayment',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Application Number',
            accessor: 'applicationNumber',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Channel',
            accessor: 'channel',
            Cell: (row: any) => {
              return <span className="">{getFormattedPaymentChannel(row.cell.value) ?? '-'}</span>;
            }
          },
          {
            Header: 'Processor',
            accessor: 'processor',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Amount',
            accessor: 'preAmount',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Charged Amount',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Processor Ref',
            accessor: 'reference',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },

          {
            Header: 'Status',
            accessor: 'paymentStatus',
            Cell: (row: any) => {
              return <PaymentStatus row={row} actions={getRowActions(row.row.original)} />;
            }
          }
        ]
      }
    ],
    []
  );

  const getPaymentActions = () => {
    let actions = [
      {
        label: 'Activate preapproval',
        callback: () => financingApplication && premiumFinancingPreapproval(financingApplication),
        icon: <ShieldCheck className="h-4 w-4" />
      },
      // {
      //   label: 'Apply discount',

      //   callback: () => console.log('Apply discount'),
      //   icon: <Percent className="h-4 w-4" />
      // }
    ];

    if (financingApplication) {
      if (!payments || payments.length === 0) {
        actions.push({
          label: 'Generate payment schedules',
          callback: () => {
            generatePremiumFinancingPayments(String(financingApplication?.applicationNumber), paymentDate)
              .then((response: any) => {
                showToast.success(response?.message || 'Payment Generated successfully.');
                invalidateQueriesPayments();
              })
              .catch((error: any) => {
                showToast.error(error?.description || 'Generate payment has failed.');
                console.log(error);
              });
          },
          icon: <CalendarClock className="h-4 w-4" />
        });
      }

      if (payments && payments?.length > 0) {
        actions.push({
          label: 'Regenerate payment schedules',
          callback: () => {
            generatePremiumFinancingPayments(String(financingApplication?.applicationNumber), paymentDate)
              .then((response: any) => {
                showToast.success(response?.message || 'Payment regenerated successfully.');
                invalidateQueriesPayments();
              })
              .catch((error: any) => {
                showToast.error(error?.description || 'Regenerate payment has failed.');
                console.log(error);
              });
          },
          icon: <RotateCcw className="h-4 w-4" />
        });
      }
    }
    return actions;
  };

  const paymentTable = () => {
    return (
      <>
        <div className=" bg-white rounded-2xl mt-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border" ref={pageRef}>
          <TabNavItem
            id="payments"
            title="Payments"
            icon={<CreditCard />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            count={payments && payments?.length}
            actions={
              <div className="cursor-pointer flex justify-center !p-0 w-10">
                <PopOnMe actions={getPaymentActions()}>
                  <EllipsisVerticalIcon className="h-6 w-6" />
                </PopOnMe>
              </div>
            }
          />

          <div className="py-3 px-4">
            <div className="grid grid-cols-3 ">
              <div className="flex">
                <span>
                  <Wallet className="w-5 h-5 mr-3" />
                </span>
                <span className="font-bold text-[15px]">Total Premium: </span>
                <span className="px-2">
                  {financingApplication?.plan?.totalAmount
                    ? financingApplication?.plan?.totalAmount.toFixed(2)
                    : '-'}
                </span>
              </div>
              <div className="flex">
                <CheckCircle className="w-5 h-5 mr-3" />
                <span className="font-bold text-[15px]">Total Paid:</span>{' '}
                <span className="px-2">
                  {' '}
                  {totalPaymentsAmount() ? totalPaymentsAmount().toFixed(2) : 0}
                </span>
              </div>
              <div className="flex">
                <CreditCard className="w-5 h-5 mr-3" />
                <span className="font-bold text-[15px]">Balance Remaining:</span>{' '}
                <span className="px-2">{balance()}</span>
              </div>
            </div>
            {loadingPayment ? (
              <div className="p-10">
                <Loader message="Fetching payments. Please wait..." brollyLoaderFixed />
              </div>
            ) : (
              <TabContent id={'payments'} activeTab={activeTab}>
                <div className="w-full relative">
                  <GTable
                    showCheckboxes
                    selectionChanged={(selectedObjects: any[]) => {
                    }}
                    isRowLinked={false}
                    stickyHeader
                    classes='!max-h-[400px]'
                    columns={columns}
                    data={orderPaymentsById}
                    tableType="payment"
                    showSearch={false}
                    loading={loadingPayment}
                    tableContainerId="tableId"
                  />
                </div>
              </TabContent>
            )}
          </div>
        </div>
      </>
    );
  };

  const getApplicationOverview = () => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-transparent dark:border-darkBg-border">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <BsShield className="h-5 w-5 mr-2 text-gray-500 dark:text-darkBg-mainText" />
            Premium Overview
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Application Number</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {financingApplication?.applicationNumber ?? '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Insurer Name</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {financingApplication?.insurer?.name ?? '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Initiated By</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {/* {financingApplication?.staff?.fullName ?? '-'} */}
                    {"-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Initiated On</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {financingApplication?.createdOn
                      ? getFormattedDate({ date: financingApplication?.createdOn, showTime: true })
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Verified On</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {financingApplication?.verifiedOn
                      ? getFormattedDate({ date: financingApplication?.verifiedOn, showTime: true })
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">
                    {financingApplication?.updatedOn
                      ? getFormattedDate({ date: financingApplication?.updatedOn, showTime: true })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const scrollToPaymentHistory = () => {
    if (pageRef.current) {
      pageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Page title={`Financing ${applicationId}`} className="">
        {loading ? (
          <div className="w-full text-center flex items-center justify-center h-screen ">
            <Loader message={`Loading data. Please wait...`} brollyLoaderFixed />
          </div>
        ) : (
          <div className=" h-fit p-5">
            <div className="flex px-3 md:px-0 flex-col md:flex-row  justify-between">
              <div className="flex w-full flex-col md:flex-row justify-between items-start mb-6">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-fit mr-4">
                      <BackButton
                        onClick={() =>
                          navigate(`${getDashboardBase()}/premium-financing`, { replace: true })
                        }
                      />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText mr-3">
                      Application-{financingApplication?.applicationNumber ?? '-'}
                    </h1>
                    <div>
                      <PolicyStatus
                        status={capitalizeSentence(financingApplication?.reviewStage ?? '-')}
                        classes="rounded-full px-2  !w-fit"
                      />
                    </div>
                  </div>

                  <p className="text-gray-600 mr-2 block md:hidden">
                    <span className="font-medium">Customer:</span>{' '}
                    <Link
                      to={`${getDashboardBase()}/customers/${financingApplication?.customer?.id}`}
                      className="text-blue-600 hover:underline">
                      {financingApplication?.customer?.firstName}{' '}
                      {financingApplication?.customer?.lastName}
                    </Link>
                  </p>
                </div>

                {user.company?.type !== COMPANY_TYPES.FINANCIER && (
                  <div className=" flex items-center justify-end gap-x-3 mb-2 min-[400px]:mb-0">
                    {getActions().length > 0 && <ActionsPopOnMe actions={getActions()} popOptionClasses="" />}
                  </div>
                )}
              </div>
            </div>
            <div className="px-3 md:px-0">
              {/* <div>{getFinancingApplication()}</div> */}

              <div className="flex flex-col gap-8 min-[1200px]:flex-row">
                <div className="flex flex-col min-[1200px]:w-3/5">
                  <div>{getApplicationOverview()}</div>

                  <div className="">{getProposer()}</div>

                  <div className="grid grid-cols-1  lg:grid-cols-2 gap-4">

                    {financingApplication?.financier && (
                      <div className="md:w-full">{getFinancedPartner()}</div>
                    )}

                    <div className="md:w-full">{getUnderwriterPartner()}</div>

                    <div className="md:w-full"> {getPaymentPlan()}</div>
                  </div>
                </div>

                {/* Activity Section */}

                <div className="md:col-span-1 w-full  min-[1200px]:w-2/5 ">
                  {/* <div>{getCustomerInfo()}</div> */}
                  <CustomerInfo
                    firstName={financingApplication?.customer?.firstName ?? ''}
                    lastName={financingApplication?.customer?.lastName ?? ''}
                    businessName={financingApplication?.customer?.businessName ?? ''}
                    email={financingApplication?.customer?.email ?? ''}
                    phoneNumber={financingApplication?.customer?.phone ?? ''}
                    otherPhone={financingApplication?.customer?.otherPhone ?? ''}
                    id={financingApplication?.customer?.id ?? ''}
                    idNumber={financingApplication?.customer.idNumber ?? ''}
                    idVerified={financingApplication?.customer?.idVerified ?? false}
                    emailVerified={financingApplication?.customer?.emailVerified ?? undefined}
                    phoneVerified={financingApplication?.customer?.phoneVerified ?? false}
                    score={financingApplication?.customer?.kycAnalysis?.score ?? undefined}
                    risk={financingApplication?.customer?.kycAnalysis?.risk ?? ''}
                    type={financingApplication?.customer?.customerType ?? undefined}
                    isClickable={true}
                    query="displayFinancingApplication"
                  />

                  <PremiumFinancingDetailsItemCard
                    title="Financing Timeline"
                    classes="grid md:grid-cols-1 max-h-[412px] overflow-y-scroll"
                    icon={<LuCalendarClock className="h-5 w-5 mr-2 text-gray-500" />}>
                    <div>
                      <ActivityStepper
                        activityStreams={financingApplication?.activityStreams ?? []}
                        orientation="vertical"
                        currentStep={0}
                      />
                    </div>
                  </PremiumFinancingDetailsItemCard>

                  <div className="mt-5">
                    <PaymentAnalysis
                      paymentAnalysis={paymentAnalysis}
                      balance={balance()}
                      handleViewFullPayment={scrollToPaymentHistory}
                      handleRunPaymentAnalysis={runPaymentAnalysis}
                    />
                  </div>
                  <div className="md:w-full">{getDocumentUpload()}</div>
                </div>
              </div>
              <div className="grid grid-cols-1  lg:grid-cols-3 gap-4"></div>
            </div>

            {paymentTable()}


            {/* Action buttons */}
            {/* <div className="flex py-4 justify-end">
              {getButtonActions()?.length > 0 && (
                <ActionsPopOnMe actions={getButtonActions()} showDropdown={true} />
              )}
            </div> */}
          </div>
        )}

        <ModalComponent />
        <PolicyModalComponent />
        <FinancingModal />
      </Page>
    </>
  );
};

export default PremiumFinancingApplicationDetails;

type ApplicationDetailsItemProps = {
  name?: string;
  value?: string | number | JSX.Element;
  children?: React.ReactNode;
  classes?: string;
};

interface IPremiumFinancingDetailsItemCard {
  title: string;
  classes?: string;
  children: JSX.Element;
  icon?: React.ReactElement;
  action?: () => void;
}

const PremiumFinancingDetailsItem = ({
  name,
  value,
  children,
  classes
}: ApplicationDetailsItemProps) => {
  return (
    <div className={clsx(`flex flex-col py-1 `, classes)}>
      <div className="text-gray-600 ">{name && `${name}:`}</div>
      <div className="flex items-center gap-1">
        <span className="truncate text-[14px]  whitespace-nowrap">{value}</span>
      </div>
      {children}
    </div>
  );
};

const PremiumFinancingDetailsItemCard = ({
  title,
  classes,
  children,
  icon,
  action
}: IPremiumFinancingDetailsItemCard): JSX.Element => {
  return (
    <div className="mt-5 bg-white rounded-2xl shadow-md overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div className="flex flex-wrap items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-transparent dark:border-darkBg-border">
        <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
          {icon}
          {title}
        </h2>
        {action && <button
          onClick={action}
          className="px-3 py-1.5 border border-gray-600  text-gray-500 dark:text-darkBg-mainText dark:border-darkBg-mainText text-xs font-medium rounded-full transition-colors duration-200 flex items-center shadow-sm"
        >
          <Upload className="h-4 w-4 mr-1.5" />
          Upload Document
        </button>
        }

      </div>
      <div
        className={clsx(
          'grid py-6  px-4 rounded-lg h-full grid-cols-1 gap-5 dark:bg-transparent dark:border-darkBg-border',
          classes ? classes : 'md:grid-cols-2 '
        )}>
        {children}
      </div>
    </div>
  );
};
